import React, { ChangeEvent, InvalidEvent, useState, useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PhoneInput from 'react-phone-input-2';

// Components
import { FieldError } from 'components/FieldError';

// Utils
import { useCombinedRef } from 'hooks/useCombinedRef';
import { useMerchant } from 'hooks/context/merchant';
import { generateId } from 'util/generateId';
import { PREFERRED_COUNTRIES_BY_LOCALE } from './utils';

// Types
import { ValidationEventHandler } from 'components/Input.type';
import { CountryCode } from 'i18n/types';
import { Customer } from 'olo-service';

// Styles
import 'react-phone-input-2/lib/bootstrap.css';
import styles from './PhoneInputField.module.scss';
import './PhoneInputField.overrides.scss';

const PHONE_NUMBER_FIELD_NAME = 'phoneNumber';
const COUNTRY_FIELD_NAME = 'country';

type PhoneInputCountryData = {
  name: string;
  dialCode: string;
  format: string;
  countryCode: string;
};

type PhoneInputFieldProps = {
  className?: string;
  label?: string;
  labelClass?: string;
  hideLabel?: boolean;
  value: string;
  onChange: (obj: { key: keyof Customer; value: string }) => void;
  onValidate: (phoneNumber: string) => boolean;
  required?: boolean;
  errorMessage?: string;
};

export const PhoneInputField: React.FC<PhoneInputFieldProps> = (props) => {
  const { className, label, labelClass, value, onChange, onValidate, errorMessage, required = false, hideLabel = false } = props;

  const { country: merchantCountry, locale } = useMerchant();

  const inputWrapperRef = useCombinedRef<HTMLDivElement>(null);

  const [isDirty, setIsDirty] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const initialValue = useRef(value || '').current;

  const generatedId = useRef(generateId()).current;
  const errorTextId = useRef(generateId()).current;
  const labelId = useRef(generateId()).current;

  const updateValue = useCallback((updatedValue?: string) => {
    if (updatedValue !== undefined) onChange({ key: PHONE_NUMBER_FIELD_NAME, value: updatedValue });
  }, [onChange]);

  const updateCountry = useCallback((updatedCountry?: CountryCode) => {
    if (updatedCountry !== undefined) onChange({ key: COUNTRY_FIELD_NAME, value: updatedCountry });
  }, [onChange]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (value !== initialValue && !isDirty) {
      setIsDirty(true);
    } else if (value === initialValue && isDirty) {
      setIsDirty(false);
    }
  });

  // NOTE: To handle initial value of the phone input field, keep value and input value in sync
  useEffect(() => {
    const inputValue = inputWrapperRef.current?.getElementsByTagName('input')[0]?.value || '';

    updateValue(inputValue.replace(/[^0-9]+/g, ''));
  }, [inputWrapperRef, updateValue]);

  useEffect(() => {
    setIsValid(onValidate(value));
  }, [onValidate, setIsValid, value]);

  useEffect(() => {
    const inputElm = inputWrapperRef?.current?.getElementsByTagName('input')[0];

    if (isValid) {
      inputElm?.setCustomValidity('');
    } else {
      inputElm?.setCustomValidity(errorMessage || 'Invalid');
    }
  }, [errorMessage, inputWrapperRef, isValid]);

  useEffect(() => {
    const inputElm = inputWrapperRef?.current?.getElementsByTagName('input')[0];

    if (isDirty) inputElm?.setAttribute('dirty', '');
    else inputElm?.removeAttribute('dirty');
  }, [inputWrapperRef, isDirty]);

  useEffect(() => {
    const inputElm = inputWrapperRef?.current?.getElementsByTagName('input')[0];

    if (isFocused) inputElm?.setAttribute('focused', '');
    else inputElm?.removeAttribute('focused');
  }, [inputWrapperRef, isFocused]);

  useEffect(() => {
    const inputElm = inputWrapperRef?.current?.getElementsByTagName('input')[0];

    if (isTouched) inputElm?.setAttribute('touched', '');
    else inputElm?.removeAttribute('touched');
  }, [inputWrapperRef, isTouched]);

  // NOTE : This package doesn't call onChange when deleting the last character in the input field
  // NOTE2: To address this, update the value to an empty string onBlur
  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement>, country: PhoneInputCountryData) => {
    setIsFocused(false);
    setIsTouched(true);

    const targetValue = e.target.value;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    const clearValue = value === '' || !targetValue || (targetValue.length === 1 && targetValue.indexOf('+') > -1);

    clearValue && updateValue('');
  }, [setIsFocused, setIsTouched, updateValue, value]);

  const handleChange = useCallback((changedValue: string, countryData: PhoneInputCountryData, e: ChangeEvent<HTMLInputElement>, formattedValue: string) => {
    updateValue(changedValue);

    const inputCountryCode = countryData?.countryCode?.toUpperCase();
    inputCountryCode && updateCountry(inputCountryCode as CountryCode);
  }, [updateCountry, updateValue]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);

  const handleValidityChange: ValidationEventHandler<HTMLInputElement> = useCallback((e: InvalidEvent<HTMLInputElement>) => {
    // See https://developer.mozilla.org/en-US/docs/Web/API/Constraint_validation#Properties
    if (onValidate) {
      setIsValid(onValidate(value));

      setIsTouched(true);
    }
  }, [onValidate, setIsTouched, setIsValid, value]);

  const wrapperClassName = clsx(styles.inputWrapper, {
    withLabel: label || !hideLabel,
    [styles.focused]: !isTouched || !!value || isFocused,
    [styles.invalid]: isTouched && !isValid,
  }, className);

  // NOTE: PhoneInput expects country to be lowercase, conversion is needed
  return (
    <div className={wrapperClassName} ref={inputWrapperRef}>
      <PhoneInput
        country={merchantCountry?.toLowerCase()}
        inputClass={styles.inputField}
        inputProps={{
          'aria-labelledby': labelId,
          'id': generatedId,
          'name': PHONE_NUMBER_FIELD_NAME,
          'onInvalid': handleValidityChange,
          required
        }}
        isValid={!isTouched || isValid}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        placeholder=""
        preferredCountries={locale ? PREFERRED_COUNTRIES_BY_LOCALE[locale] : []}
        value={value}
      />
      {!hideLabel && label && <label id={labelId} htmlFor={generatedId} className={labelClass}>{label}</label>}
      {isTouched && !isValid && errorMessage && <FieldError id={errorTextId} className="error-text">{errorMessage}</FieldError>}
    </div>
  );
};
